import React, { FC, useState } from "react";
import BSButton from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Stack from "react-bootstrap/Stack";
import {
  useAddOrUpdateManagedUpdateMutation,
  useGetManagedUpdatesListQuery,
} from "features/api/contentManagement/contentManagementApiSlice";
import * as Yup from "yup";
import { PencilSquare } from "react-bootstrap-icons";
import { ERROR_MESSAGE } from "components/Reports/reportAssets/assets";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Button } from "./Button";
import RenderList from "./List/RenderList";
import AddUpdatesContentForm from "./AddUpdatesContentForm";
import {
  UpdateFormValues,
  UpdateFormValidations,
} from "../contentManagementUtils/tabSchema";
import {
  getAdjustedISOString,
  getFormattedDateAndTime,
} from "../contentManagementUtils/contentManagementUtils";

const UpdatesTab: FC = () => {
  const { t } = useTranslation();
  const { data: updates } = useGetManagedUpdatesListQuery();

  const [addOrUpdateManagedUpdate] = useAddOrUpdateManagedUpdateMutation();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] =
    useState<UpdateFormValues | null>(null);

  const toggleCreateForm = () => {
    setShowCreateForm(!showCreateForm);
  };

  const toggleUpdateForm = (initialValues: UpdateFormValues | null) => {
    setShowUpdateForm(initialValues);
  };

  const updatesValidationSchema: Yup.ObjectSchema<UpdateFormValidations> =
    Yup.object().shape({
      headLine: Yup.string().required(t("חובה להזין כותרת")),
      content: Yup.string().required(t("חובה להזין תוכן")),
    });

  const updatesInitialValues: UpdateFormValues = {
    id: 0,
    createdOn: new Date(),
    showInApp: false,
    headLine: "",
    content: "",
    url: "",
    isPushNotification: false,
    isPositiveUpdate: false,
    isActive: true,
    expirationDate: new Date(),
    fileName: "",
    file: null,
  };

  const handleCreateOrUpdate: (values: UpdateFormValues) => void = async (
    values: UpdateFormValues
  ) => {
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (key !== "file" && key !== "expirationDate" && key !== "createdOn") {
        formData.append(key, value.toString());
      }
    });
    formData.append("file", values.file);

    formData.append(
      "expirationDate",
      getAdjustedISOString(values.expirationDate)
    );

    formData.append("createdOn", getAdjustedISOString(values.createdOn));
    try {
      await addOrUpdateManagedUpdate(formData).unwrap();
      // close modal
      values.id === 0 ? toggleCreateForm() : toggleUpdateForm(null);
    } catch (e) {
      console.error("Error:", e);
      if (e?.status === 400) {
        if (Array.isArray(e?.data?.errors)) {
          e?.data?.errors?.map((error) =>
            toast.error(error.errorMessage, {
              style: { direction: "rtl" },
            })
          );
        } else if (e?.data) {
          toast.error(e?.data.Message, {
            style: { direction: "rtl" },
          });
        }
      } else {
        toast.error(ERROR_MESSAGE);
      }
    }
  };

  const columns = [
    {
      title: t("creationDate"),
      dataIndex: 1,
      key: "createdOn",
      flex: 1,
      renderCell: (text: string) => {
        const formatted = getFormattedDateAndTime(text);
        return <span>{formatted}</span>;
      },
      asc: true,
      minWidth: 150,
    },
    {
      title: t("מע'\\אפליקציה"),
      dataIndex: 2,
      key: "showInApp",
      flex: 1,
      renderCell: (isShowInApp: boolean) => (
        <span>{isShowInApp ? t("application") : t("systemShort")}</span>
      ),
      asc: true,
      minWidth: 150,
    },
    {
      title: t("כותרת"),
      dataIndex: 3,
      key: "headLine",
      flex: 1,
      renderCell: (text: string) => (
        <span>{text !== "undefined" ? text : ""}</span>
      ),
      asc: true,
      minWidth: 150,
    },
    {
      title: t("contentLabel"),
      dataIndex: 4,
      key: "content",
      flex: 1,
      renderCell: (text: string) => {
        const truncatedText =
          text.length > 24 ? `${text.slice(0, 24)}...` : text;

        return (
          <span title={text} style={{ cursor: "help" }}>
            {truncatedText}
          </span>
        );
      },
      asc: true,
      minWidth: 150,
    },
    {
      title: t("ידיעת PUSH?"),
      dataIndex: 5,
      key: "isPushNotification",
      flex: 1,
      renderCell: (isPush: boolean) => {
        return (
          <Stack direction="horizontal" gap={0}>
            <Form.Check
              type="checkbox"
              id="custom-checkbox"
              checked={isPush}
              disabled
              size={20}
            />
          </Stack>
        );
      },
      asc: true,
      minWidth: 150,
    },
    {
      title: t("activeQuestion"),
      dataIndex: 6,
      key: "isActive",
      flex: 1,
      renderCell: (isActive: boolean) => {
        return (
          <Stack direction="horizontal" gap={0}>
            <Form.Check
              type="checkbox"
              id="custom-checkbox"
              checked={isActive}
              disabled
              size={20}
            />
          </Stack>
        );
      },
      asc: true,
      minWidth: 150,
    },
    {
      title: "",
      dataIndex: 7,
      key: "actions",
      flex: 1,
      renderCell: (update: UpdateFormValues) => {
        return (
          <Stack direction="horizontal" gap={0}>
            <BSButton key={update.id} variant="outline">
              <PencilSquare
                size={20}
                color="primary"
                onClick={() => {
                  toggleUpdateForm({ ...update, url: update.url ?? "" });
                }}
              />
            </BSButton>
          </Stack>
        );
      },
      asc: true,
      minWidth: 150,
    },
  ];

  return (
    <div className="tab-list-container">
      <div className="tab-list-header">
        <Button title={t("add")} handleClick={toggleCreateForm} />
      </div>
      <RenderList<UpdateFormValues>
        data={updates}
        columns={columns}
        disableField="disable"
      />
      {showCreateForm && (
        <AddUpdatesContentForm
          isOpen={showCreateForm}
          toggle={toggleCreateForm}
          submitForm={handleCreateOrUpdate}
          headerTitle={t("ידיעה חדשה")}
          approveBtnLabel={t("יצירת ידיעה")}
          cancelBtnLabel={t("cancel")}
          initialValues={updatesInitialValues}
          validationSchema={updatesValidationSchema}
        />
      )}
      {!!showUpdateForm && (
        <AddUpdatesContentForm
          isOpen={!!showUpdateForm}
          toggle={toggleUpdateForm}
          submitForm={handleCreateOrUpdate}
          headerTitle={t("עדכן ידיעה")}
          approveBtnLabel={t("עדכן ידיעה")}
          cancelBtnLabel={t("cancel")}
          initialValues={showUpdateForm}
          validationSchema={updatesValidationSchema}
        />
      )}
    </div>
  );
};

export default UpdatesTab;
